import { useEffect } from 'react';

// ============================================================
// useViewportHeightSync -- keep --app-vh in sync with the visible
// viewport so iOS keyboard / dynamic toolbar don't push our
// fixed-height surfaces below the visible area.
// ============================================================
// CSS `100vh` on iOS Safari and on Capacitor WKWebView refers to
// the *layout* viewport, which doesn't shrink when the on-screen
// keyboard opens — so a `h-[calc(100vh-3.5rem)]` container keeps
// its "tall" height and the input ends up hidden under the
// keyboard.
//
// This hook subscribes to `window.visualViewport` (the
// keyboard-aware viewport) and writes its current `height` to a
// CSS custom property `--app-vh` on the root element. Tailwind
// arbitrary-value classes use `var(--app-vh,100vh)` so the
// fallback preserves the pre-hook behavior during the first paint
// and on browsers without `visualViewport` support.
//
// Mount once at app root. Side-effect only, no return value.

export function useViewportHeightSync(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    const sync = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      root.style.setProperty('--app-vh', `${h}px`);
    };

    sync();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', sync);
      // visualViewport.scroll fires when the page scrolls under a
      // pinned keyboard; we want our pinned containers to track that.
      vv.addEventListener('scroll', sync);
      return () => {
        vv.removeEventListener('resize', sync);
        vv.removeEventListener('scroll', sync);
      };
    }

    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);
}
